'use client'

import React, { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui'
import { Card, CardContent } from '@/components/ui'
import { Crown } from 'lucide-react'
import { gojuonData, dakutenData, type Kana } from '@/data/gojuon'
import { playGojuonAudio, getRomajiFromKana } from '@/utils/gojuonAudio'
import { findSimilarKanasFromPool } from '@/utils/gojuonSimilarity'
import { getDefaultTrialVoiceId } from '@/config/trial'
import { playAudioIOSOptimized } from '@/utils/audioPlayer'
import { PlanSelectionModal } from '@/components/subscription/PlanSelectionModal'

// 試用版：使用試用版專用的進度 hook（與正式版邏輯一致，但使用不同的 localStorage key）
const useTrialGojuonProgress = () => {
  const [progress, setProgress] = useState<{
    hiragana: Record<string, { learned: boolean; practiced: boolean; written: boolean; failed?: boolean; lastUpdated?: number }>
    katakana: Record<string, { learned: boolean; practiced: boolean; written: boolean; failed?: boolean; lastUpdated?: number }>
  }>({
    hiragana: {},
    katakana: {}
  })

  useEffect(() => {
    const saved = localStorage.getItem('trial_gojuon_progress')
    if (saved) {
      try {
        const parsed = JSON.parse(saved)
        const normalized: typeof progress = {
          hiragana: {},
          katakana: {}
        }
        
        if (parsed.hiragana) {
          Object.keys(parsed.hiragana).forEach(key => {
            normalized.hiragana[key] = {
              learned: parsed.hiragana[key].learned || false,
              practiced: parsed.hiragana[key].practiced || false,
              written: parsed.hiragana[key].written || false,
              failed: parsed.hiragana[key].failed || false,
              lastUpdated: parsed.hiragana[key].lastUpdated || 0
            }
          })
        }
        
        if (parsed.katakana) {
          Object.keys(parsed.katakana).forEach(key => {
            normalized.katakana[key] = {
              learned: parsed.katakana[key].learned || false,
              practiced: parsed.katakana[key].practiced || false,
              written: parsed.katakana[key].written || false,
              failed: parsed.katakana[key].failed || false,
              lastUpdated: parsed.katakana[key].lastUpdated || 0
            }
          })
        }
        
        setProgress(normalized)
      } catch (e) {
        console.error('Failed to load trial gojuon progress:', e)
      }
    }
  }, [])

  useEffect(() => {
    if (Object.keys(progress.hiragana).length > 0 || Object.keys(progress.katakana).length > 0) {
      try {
        localStorage.setItem('trial_gojuon_progress', JSON.stringify(progress))
      } catch (e) {
        console.error('Failed to save trial gojuon progress:', e)
      }
    }
  }, [progress])

  const updateProgress = (
    kanaKey: string,
    type: 'learn' | 'practice' | 'write' | 'fail',
    mode: 'hiragana' | 'katakana'
  ) => {
    setProgress(prev => {
      const newProgress = { ...prev }
      const prevModeMap = newProgress[mode] || {}
      const current = prevModeMap[kanaKey] || {
        learned: false,
        practiced: false,
        written: false,
        failed: false,
        lastUpdated: 0
      }

      const updated = {
        ...current,
        [type === 'learn' ? 'learned' : type === 'practice' ? 'practiced' : type === 'write' ? 'written' : 'failed']: true,
        ...(type === 'practice' ? { failed: false } : {}),
        lastUpdated: Date.now()
      }

      return {
        ...newProgress,
        [mode]: {
          ...prevModeMap,
          [kanaKey]: updated
        }
      }
    })
  }

  return { progress, updateProgress }
}

const MAX_WRONG_REPETITIONS = 2
const RECENT_HISTORY_LIMIT = 2
const TRIAL_TOTAL_QUESTIONS = 10

const GojuonPracticeTrialPage = () => {
  const router = useRouter()
  const { progress, updateProgress } = useTrialGojuonProgress()
  const [isPlaying, setIsPlaying] = useState(false)
  const [isHiragana, setIsHiragana] = useState(true)
  const [currentQuestion, setCurrentQuestion] = useState<Kana | null>(null)
  const [options, setOptions] = useState<Kana[]>([])
  const [questionId, setQuestionId] = useState(0) // 用於強制每題按鈕重新掛載，避免預選殘留
  const [score, setScore] = useState({ correct: 0, total: 0 })
  const [showResult, setShowResult] = useState(false)
  const [feedback, setFeedback] = useState<'correct' | 'incorrect' | null>(null)
  const [selectedOption, setSelectedOption] = useState<Kana | null>(null)
  const [wrongAnswers, setWrongAnswers] = useState<Set<string>>(new Set())
  const [wrongAnswerCounts, setWrongAnswerCounts] = useState<Record<string, number>>({})
  const [recentQuestions, setRecentQuestions] = useState<string[]>([])
  const [appearedSet, setAppearedSet] = useState<Set<string>>(new Set())
  const [masteredThisRound, setMasteredThisRound] = useState<Set<string>>(new Set())
  const [showPlanModal, setShowPlanModal] = useState(false)
  // 方案 1A：使用 ref 追蹤按鈕元素，用於清除 iOS focus 狀態
  const buttonRefs = useRef<(HTMLButtonElement | null)[]>([])
  // 方案 D：追蹤當前題目 ID 和音檔，用於防止播放錯誤音檔
  const currentQuestionIdRef = useRef<number>(0)
  const currentAudioRef = useRef<HTMLAudioElement | null>(null)
  // 方案 3：透明覆蓋層 ref，用於清除 iOS 預選佔留
  const overlayRef = useRef<HTMLDivElement | null>(null)
  // 方案 A：配額機制 - 追蹤已使用的錯題數量，確保至少30%是錯題
  const [wrongQuestionsUsed, setWrongQuestionsUsed] = useState(0)
  const WRONG_QUESTION_QUOTA = Math.ceil(TRIAL_TOTAL_QUESTIONS * 0.3) // 至少30%是錯題

  const openPlanModal = () => {
    setShowPlanModal(true)
  }

  const closePlanModal = () => {
    setShowPlanModal(false)
  }

  const allKana = [...Object.values(gojuonData).flat(), ...Object.values(dakutenData).flat()].filter((k): k is Kana => !!k && !!k.hiragana && !!k.katakana)

  const generateQuestion = async (playSound = false) => {
    // 方案 D：取消正在播放的音檔
    if (currentAudioRef.current) {
      try {
        currentAudioRef.current.pause()
        currentAudioRef.current.currentTime = 0
        currentAudioRef.current = null
      } catch (e) {
        // 忽略取消錯誤
      }
      setIsPlaying(false)
    }
    
    // 方案 3：透明覆蓋層點擊 - 清除 iOS 預選佔留
    // 在生成新題目前，先點擊透明覆蓋層來清除上一題的 focus 殘留
    if (overlayRef.current) {
      try {
        // 確保覆蓋層可以接收點擊事件
        overlayRef.current.style.pointerEvents = 'auto'
        // 使用 requestAnimationFrame 確保在下一幀執行，讓瀏覽器有時間處理
        requestAnimationFrame(() => {
          if (overlayRef.current) {
            // programmatically 點擊覆蓋層
            overlayRef.current.click()
            // 點擊後立即恢復 pointer-events: none，避免阻擋其他交互
            setTimeout(() => {
              if (overlayRef.current) {
                overlayRef.current.style.pointerEvents = 'none'
              }
            }, 10)
          }
        })
      } catch (e) {
        // 忽略錯誤，不影響主要功能
      }
    }
    
    // 方案 1A：加強按鈕重置，解決 iOS 預選佔留問題
    // 1. 清除 document 的 activeElement
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    // 2. 清除所有按鈕的 focus 狀態
    buttonRefs.current.forEach(btn => {
      if (btn && btn instanceof HTMLElement) {
        btn.blur()
      }
    })
    // 3. 重置按鈕 refs 陣列
    buttonRefs.current = []
    
    // 方案 D：更新當前題目 ID
    const thisQuestionId = currentQuestionIdRef.current + 1
    currentQuestionIdRef.current = thisQuestionId

    let questionKana: Kana
    let selectedFromWrongPool = false
    
    const wrongKanaPool = allKana.filter(kana => wrongAnswers.has(kana.hiragana))
    const keyOf = (k: Kana) => (isHiragana ? k.hiragana : k.katakana)
    const recentSet = new Set(recentQuestions)
    const unseenPool = allKana.filter(k => 
      !appearedSet.has(keyOf(k)) && 
      !wrongAnswers.has(k.hiragana) &&
      !masteredThisRound.has(k.hiragana)
    )

    const wrongPoolWithinLimit = wrongKanaPool.filter(kana => (wrongAnswerCounts[kana.hiragana] || 0) < MAX_WRONG_REPETITIONS)
    const wrongPoolWithInterval = wrongPoolWithinLimit.filter(kana => !recentSet.has(keyOf(kana)))
    const availableWrongPool = wrongPoolWithInterval.length > 0 ? wrongPoolWithInterval : wrongPoolWithinLimit

    if (availableWrongPool.length > 0 && unseenPool.length > 0) {
      // 方案 A：配額機制 - 平衡錯題和新題的比例
      const needsMoreWrongQuestions = wrongQuestionsUsed < WRONG_QUESTION_QUOTA
      // 即使配額未達標，也要有 30% 機率選擇新題，避免連續出錯題
      // 配額已達標後，降低錯題機率到 40%，讓新題佔 60%
      const wrongProbability = needsMoreWrongQuestions ? 0.7 : 0.4
      const pickWrong = Math.random() < wrongProbability
      const pool = pickWrong ? availableWrongPool : unseenPool
      questionKana = pool[Math.floor(Math.random() * pool.length)]
      // 如果選擇的是錯題池，則一定是錯題
      selectedFromWrongPool = pickWrong && pool === availableWrongPool
    } else if (availableWrongPool.length > 0) {
      // 只有錯誤題可用，但需要檢查配額
      const needsMoreWrongQuestions = wrongQuestionsUsed < WRONG_QUESTION_QUOTA
      if (needsMoreWrongQuestions) {
        // 配額未達標，選擇錯題
        questionKana = availableWrongPool[Math.floor(Math.random() * availableWrongPool.length)]
        selectedFromWrongPool = true
      } else {
        // 配額已達標，但沒有其他選項，重置循環並排除錯題和本輪已掌握題目
        setAppearedSet(new Set())
        setRecentQuestions([])
        setMasteredThisRound(new Set())
        // 從所有假名中排除錯題和本輪已掌握題目，然後選擇
        const nonWrongKana = allKana.filter(kana => 
          !wrongAnswers.has(kana.hiragana) && 
          !masteredThisRound.has(kana.hiragana)
        )
        if (nonWrongKana.length > 0) {
          questionKana = nonWrongKana[Math.floor(Math.random() * nonWrongKana.length)]
          selectedFromWrongPool = false
        } else {
          // 如果所有假名都是錯題或已掌握，只能選擇錯題（但這不應該發生）
          questionKana = availableWrongPool[Math.floor(Math.random() * availableWrongPool.length)]
          selectedFromWrongPool = true
        }
      }
    } else if (unseenPool.length > 0) {
      // 只有未出現題可用，但如果配額未達標且還有錯題，有一定機率選擇錯題
      const needsMoreWrongQuestions = wrongQuestionsUsed < WRONG_QUESTION_QUOTA
      if (needsMoreWrongQuestions && wrongKanaPool.length > 0) {
        // 配額未達標，但錯題池被過濾空了（可能是因為重複限制），重置限制
        // 但即使重置後，也要有 50% 機率選擇新題，避免連續出錯題
        setWrongAnswerCounts({})
        setRecentQuestions([])
        const resetWrongPool = wrongKanaPool.filter(kana => !recentSet.has(keyOf(kana)))
        if (resetWrongPool.length > 0 && Math.random() < 0.5) {
          // 50% 機率選擇錯題
          questionKana = resetWrongPool[Math.floor(Math.random() * resetWrongPool.length)]
          selectedFromWrongPool = true
        } else {
          // 50% 機率選擇新題，或重置後錯題池為空
          questionKana = unseenPool[Math.floor(Math.random() * unseenPool.length)]
          selectedFromWrongPool = false
        }
      } else {
        // 配額已達標或沒有錯題，選擇未出現題
        questionKana = unseenPool[Math.floor(Math.random() * unseenPool.length)]
        selectedFromWrongPool = false
      }
    } else {
      // 所有題目都出現過且沒有錯題，從本輪尚未完全掌握的題目中選擇
      const notMasteredPool = allKana.filter(k => !masteredThisRound.has(k.hiragana))

      if (notMasteredPool.length > 0) {
        questionKana = notMasteredPool[Math.floor(Math.random() * notMasteredPool.length)]
      } else {
        // 本輪全部都已掌握，重置狀態重新開始循環
        setAppearedSet(new Set())
        setRecentQuestions([])
        setMasteredThisRound(new Set())
        questionKana = allKana[Math.floor(Math.random() * allKana.length)]
      }
    }

    if (selectedFromWrongPool) {
      // 方案 A：增加錯題使用計數
      setWrongQuestionsUsed(prev => prev + 1)
      setWrongAnswerCounts(prev => ({
        ...prev,
        [questionKana.hiragana]: (prev[questionKana.hiragana] || 0) + 1
      }))
    }

    setCurrentQuestion(questionKana)
    setQuestionId(prev => prev + 1)
    setAppearedSet(prev => new Set([...prev, keyOf(questionKana)]))
    setSelectedOption(null)
    setFeedback(null)

    // 生成選項（包括正確答案）
    // 70% 選擇容易混淆的假名（視覺相似或發音相似），30% 隨機
    let optionsList = [questionKana]
    const similarKanas = findSimilarKanasFromPool(questionKana, allKana, isHiragana ? 'hiragana' : 'katakana')
    const neededOptions = 5 // 總共需要6個選項（1個正確答案 + 5個錯誤選項）
    const similarCount = Math.min(Math.floor(neededOptions * 0.7), similarKanas.length) // 70% 相似
    const randomCount = neededOptions - similarCount // 30% 隨機
    
    // 添加相似假名（打亂順序）
    const shuffledSimilar = [...similarKanas].sort(() => Math.random() - 0.5).slice(0, similarCount)
    optionsList.push(...shuffledSimilar)
    
    // 添加隨機假名
    const remainingKanas = allKana.filter(
      k => k.hiragana !== questionKana.hiragana && 
           !optionsList.some(opt => opt.hiragana === k.hiragana)
    )
    const shuffledRandom = [...remainingKanas].sort(() => Math.random() - 0.5).slice(0, randomCount)
    optionsList.push(...shuffledRandom)
    
    // 如果還不夠，用隨機填充
    while (optionsList.length < 6) {
      const randomKana = allKana[Math.floor(Math.random() * allKana.length)]
      if (randomKana && !optionsList.some(k => k.hiragana === randomKana.hiragana)) {
        optionsList.push(randomKana)
      }
    }
    
    setOptions(optionsList.sort(() => Math.random() - 0.5))

    // 方案 D：播放音檔前驗證題目是否仍為當前題目
    if (playSound) {
      // 保存當前題目的 kana 和 ID，用於驗證
      const kanaToPlay = isHiragana ? questionKana.hiragana : questionKana.katakana
      const questionIdWhenPlay = thisQuestionId
      
      setIsPlaying(true)
      
      // 方案 D：驗證函數：檢查題目是否仍為當前題目
      const isValid = () => {
        return currentQuestionIdRef.current === questionIdWhenPlay
      }
      
      // 方案 D：直接使用 playGojuonAudio，但在內部獲取 Audio 對象
      // 由於 playGojuonAudio 內部使用 playAudioIOSOptimized，我們需要包裝它
      // 創建一個包裝的播放函數
      const playWithValidation = async () => {
        try {
          // 獲取 voice_id 和 romaji
          const voiceId = getDefaultTrialVoiceId()
          const romaji = getRomajiFromKana(kanaToPlay)
          
          if (!romaji) {
            // 沒有 romaji，使用 VOICEVOX
            const { playJapaneseText } = await import('@/utils/speech')
            await playJapaneseText(kanaToPlay, 'gojuon', 'female')
            if (isValid()) setIsPlaying(false)
            return
          }
          
          const audioUrl = `https://tts.jlpt-aitutor.com/audio-files/gojuon/voice_${voiceId}/${romaji}.wav`
          
          // 創建 Audio 對象並保存到 ref（用於取消）
          const audio = new Audio(audioUrl)
          audio.preload = 'auto'
          currentAudioRef.current = audio
          
          // 監聽事件
          const handleCanPlay = () => {
            if (!isValid()) {
              audio.pause()
              currentAudioRef.current = null
              setIsPlaying(false)
              return
            }
            audio.play().catch(() => {
              // 播放失敗，降級到 VOICEVOX
              if (isValid()) {
                import('@/utils/speech').then(({ playJapaneseText }) => {
                  playJapaneseText(kanaToPlay, 'gojuon', 'female')
                    .then(() => {
                      if (isValid()) setIsPlaying(false)
                    })
                    .catch(() => {
                      if (isValid()) setIsPlaying(false)
                    })
                })
              }
            })
          }
          
          const handleEnded = () => {
            if (isValid()) setIsPlaying(false)
            currentAudioRef.current = null
          }
          
          const handleError = async () => {
            if (isValid()) {
              try {
                const { playJapaneseText } = await import('@/utils/speech')
                await playJapaneseText(kanaToPlay, 'gojuon', 'female')
              } catch (err) {
                // 忽略錯誤
              }
              setIsPlaying(false)
            }
            currentAudioRef.current = null
          }
          
          audio.addEventListener('canplaythrough', handleCanPlay, { once: true })
          audio.addEventListener('ended', handleEnded, { once: true })
          audio.addEventListener('error', handleError, { once: true })
          
          // 開始載入
          audio.load()
        } catch (error) {
          if (isValid()) setIsPlaying(false)
          currentAudioRef.current = null
        }
      }
      
      playWithValidation()
    }

    setRecentQuestions(prev => {
      const next = [...prev, keyOf(questionKana)]
      if (next.length > RECENT_HISTORY_LIMIT) {
        next.shift()
      }
      return next
    })
  }

  // 從 progress 初始化錯題列表（試用版使用 useTrialGojuonProgress）
  useEffect(() => {
    if (!progress) return
    
    const modeProgress = progress[isHiragana ? 'hiragana' : 'katakana'] || {}
    const wrongSet = new Set<string>()
    
    // 從進度中讀取所有 failed: true 的假名
    Object.keys(modeProgress).forEach(kanaKey => {
      if (modeProgress[kanaKey]?.failed) {
        wrongSet.add(kanaKey)
      }
    })
    
    setWrongAnswers(wrongSet)
  }, [progress, isHiragana])

  // 追蹤是否為初始載入
  const [hasUserInteracted, setHasUserInteracted] = useState(false)

  useEffect(() => {
    // 方案 A：切換假名類型時重置錯題配額計數
    setWrongQuestionsUsed(0)
    // 初始載入時不播放音頻（iOS 限制），切換假名類型時播放（用戶操作後）
    generateQuestion(hasUserInteracted)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHiragana, hasUserInteracted]) // generateQuestion 只在 isHiragana 或 hasUserInteracted 改變時重新生成題目

  // 監聽用戶交互，標記為已交互
  useEffect(() => {
    const handleUserInteraction = () => {
      setHasUserInteracted(true)
    }
    
    // 監聽各種用戶交互事件
    window.addEventListener('touchstart', handleUserInteraction, { once: true })
    window.addEventListener('click', handleUserInteraction, { once: true })
    
    return () => {
      window.removeEventListener('touchstart', handleUserInteraction)
      window.removeEventListener('click', handleUserInteraction)
    }
  }, [])

  const handleAnswer = (option: Kana) => {
    if (selectedOption || !currentQuestion) return

    setSelectedOption(option)
    const isCorrect = option.hiragana === currentQuestion.hiragana
    setFeedback(isCorrect ? 'correct' : 'incorrect')
    
    // 使用 setScore 的回調來獲取新的 total 值，避免閉包問題
    setScore(prev => {
      const newTotal = prev.total + 1
      
      // 方案 3A：正確記錄進度到已掌握和錯誤分類
      if (isCorrect) {
        const kanaKey = isHiragana ? currentQuestion.hiragana : currentQuestion.katakana
        // 答對時：標記為已掌握（practiced: true），並清除失敗標記（failed: false）
        updateProgress(kanaKey, 'practice', isHiragana ? 'hiragana' : 'katakana')
        setWrongAnswers(prev => {
          const next = new Set(prev)
          next.delete(currentQuestion.hiragana)
          return next
        })
        setWrongAnswerCounts(prev => {
          if (!(currentQuestion.hiragana in prev)) return prev
          const { [currentQuestion.hiragana]: _, ...rest } = prev
          return rest
        })
        setMasteredThisRound(prev => {
          const next = new Set(prev)
          next.add(currentQuestion.hiragana)
          return next
        })
      } else {
        const kanaKey = isHiragana ? currentQuestion.hiragana : currentQuestion.katakana
        // 答錯時：標記為錯誤（failed: true），會在五十音表中顯示為橘色
        updateProgress(kanaKey, 'fail', isHiragana ? 'hiragana' : 'katakana')
        setWrongAnswers(prev => new Set([...prev, currentQuestion.hiragana]))
        // 注意：wrongAnswerCounts 只在題目被選中時才增加（在 generateQuestion 中處理）
      }

      setTimeout(() => {
        // 方案 1A：在清除狀態時也清除所有按鈕的 focus，防止 iOS 預選殘留
        buttonRefs.current.forEach(btn => {
          if (btn && btn instanceof HTMLElement) {
            btn.blur()
          }
        })
        if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
          document.activeElement.blur()
        }
        
        // 先明確清除選擇狀態，確保下一題不會預選答案
        setSelectedOption(null)
        setFeedback(null)
        
        // 方案 2A：10題後顯示結果頁面，否則生成新題
        if (newTotal >= TRIAL_TOTAL_QUESTIONS) {
          // 方案 A：測驗結束時重置錯題配額計數
          setWrongQuestionsUsed(0)
          // 顯示結果頁面
          setShowResult(true)
        } else {
          // 方案A：在清除狀態後使用下一個微任務再產生新題，避免競態造成預選
          setTimeout(() => {
            generateQuestion(true)
          }, 0)
        }
      }, 1500)
      
      return { 
        correct: prev.correct + (isCorrect ? 1 : 0), 
        total: newTotal
      }
    })
  }

  // 方案 2A：結果頁面
  if (showResult) {
    return (
      <div className="min-h-screen bg-gray-900 text-white p-4 sm:p-8">
        <div className="max-w-md mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6">練習完成！</h2>
          <p className="text-2xl mb-4">得分：{score.correct} / {score.total}</p>
          <p className="text-xl mb-8">
            正確率：{Math.round((score.correct / score.total) * 100)}%
          </p>
          {wrongAnswers.size > 0 && (
            <div className="mb-8">
              <p className="text-xl mb-4 text-orange-400">
                有 {wrongAnswers.size} 個假名需要複習
              </p>
            </div>
          )}
          {/* 再來一輪按鈕 - 小按鈕，置中 */}
          <div className="mb-8">
            <Button 
              onClick={() => {
                // 完整狀態重置，開始新一輪
                // 1. 重置所有狀態變數
                setScore({ correct: 0, total: 0 })
                setShowResult(false)
                setSelectedOption(null) // 關鍵：清除選擇狀態，避免按鈕被禁用
                setFeedback(null) // 清除回饋狀態
                setCurrentQuestion(null)
                setOptions([])
                setIsPlaying(false)
                setWrongAnswers(new Set())
                setWrongAnswerCounts({})
                setAppearedSet(new Set())
                setRecentQuestions([])
                setMasteredThisRound(new Set())
                setWrongQuestionsUsed(0)
                setQuestionId(0) // 重置題目 ID，強制按鈕重新掛載
                
                // 2. 重置所有 refs
                buttonRefs.current = []
                currentQuestionIdRef.current = 0
                if (currentAudioRef.current) {
                  try {
                    currentAudioRef.current.pause()
                    currentAudioRef.current.currentTime = 0
                    currentAudioRef.current = null
                  } catch (e) {
                    // 忽略錯誤
                  }
                }
                
                // 3. 清除所有按鈕的 focus 狀態
                if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
                  document.activeElement.blur()
                }
                
                // 4. 生成新題目
                setTimeout(() => {
                  generateQuestion(true)
                }, 0)
              }}
            >
              再來一輪
            </Button>
          </div>
          
          {/* 返回按鈕 - 灰色按鈕，畫面右下靠左 */}
          <div className="mt-6 text-left">
            <Button 
              onClick={() => router.push('/try/gojuon')}
              className="bg-gray-600 hover:bg-gray-700 text-white"
            >
              ← 返回
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 sm:p-8">
      {/* 方案選擇彈窗 */}
      <PlanSelectionModal
        isOpen={showPlanModal}
        onClose={closePlanModal}
      />

      {/* 方案 3：透明覆蓋層 - 用於清除 iOS 預選佔留 */}
      <div
        ref={overlayRef}
        className="fixed inset-0 z-40 opacity-0 pointer-events-none"
        onClick={(e) => {
          // 點擊覆蓋層時，確保清除所有 focus
          e.preventDefault()
          e.stopPropagation()
          if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur()
          }
        }}
        style={{
          pointerEvents: 'auto', // 允許點擊
          opacity: 0, // 完全透明
          zIndex: 40 // 低於彈窗的 z-50
        }}
        aria-hidden="true"
      />

      <div className="max-w-4xl mx-auto">
        {/* 試用版提示 */}
        <Card className="mb-6 border-yellow-500 bg-yellow-900/20">
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <span className="font-semibold">50音完全免費</span>
                <span className="text-sm text-gray-300">N5~N1 學習內容需訂閱解鎖</span>
              </div>
              <Button
                onClick={openPlanModal}
                size="sm"
                className="bg-gradient-to-r from-purple-600 to-blue-600"
              >
                <Crown className="h-4 w-4 mr-2" />
                立即訂閱解鎖 N5~N1
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold">識別練習（試用版）</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          <div className="flex gap-4 mb-6 justify-center">
            <Button
              onClick={() => setIsHiragana(true)}
              variant={isHiragana ? 'selected' : 'default'}
            >
              平假名
            </Button>
            <Button
              onClick={() => setIsHiragana(false)}
              variant={!isHiragana ? 'selected' : 'default'}
            >
              片假名
            </Button>
          </div>

          {currentQuestion && (
            <Card className="mb-6">
              <CardContent className="p-8 text-center">
                <div className="mb-6">
                  <p className="text-6xl font-bold mb-4">
                    {currentQuestion.romaji}
                  </p>
                  <Button
                    onClick={() => {
                      setIsPlaying(true)
                      playGojuonAudio(
                        isHiragana ? currentQuestion.hiragana : currentQuestion.katakana,
                        () => setIsPlaying(false),
                        () => setIsPlaying(false),
                        () => setIsPlaying(false),
                        true // 試用版模式
                      )
                    }}
                    disabled={isPlaying}
                    variant="secondary"
                  >
                    {isPlaying ? '播放中...' : '🔊 播放發音'}
                  </Button>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  {options.map((option, idx) => {
                    const isSelected = selectedOption?.hiragana === option.hiragana
                    const isCorrect = option.hiragana === currentQuestion.hiragana
                    const showFeedback = selectedOption !== null

                    let bgColor = 'bg-gray-800'
                    if (showFeedback) {
                      if (isSelected && isCorrect) bgColor = 'bg-indigo-600'
                      else if (isSelected && !isCorrect) bgColor = 'bg-orange-500'
                      else if (!isSelected && isCorrect) bgColor = 'bg-indigo-500'
                    }

                    return (
                      <Button
                        key={`${questionId}-${idx}`}
                        ref={(el) => {
                          if (el) buttonRefs.current[idx] = el
                        }}
                        onClick={() => handleAnswer(option)}
                        onMouseDown={(e) => {
                          // 方案 1A：防止 iOS 的 focus 狀態殘留
                          e.preventDefault()
                        }}
                        onTouchStart={(e) => {
                          // 方案 1A：防止 iOS 的 touch 事件造成 focus 殘留
                          const target = e.currentTarget
                          setTimeout(() => {
                            if (target) target.blur()
                          }, 0)
                        }}
                        disabled={!!selectedOption}
                        tabIndex={-1}
                        className={`${bgColor} h-16 sm:h-20 text-xl sm:text-2xl min-h-[60px] focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 outline-none`}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        {isHiragana ? option.hiragana : option.katakana}
                      </Button>
                    )
                  })}
                </div>

                {feedback && (
                  <div className={`mt-4 text-lg font-bold ${feedback === 'correct' ? 'text-indigo-400' : 'text-orange-400'}`}>
                    {feedback === 'correct' ? '✓ 正確！' : '✗ 錯誤'}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-4">
              <div className="flex justify-between items-center">
                <span>正確率：{score.total > 0 ? Math.round((score.correct / score.total) * 100) : 0}%</span>
                <span>已答：{score.total} / {TRIAL_TOTAL_QUESTIONS} 題</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 返回按鈕 - 頁面最後，靠左對齊 */}
        <div className="mt-6">
          <Button 
            onClick={() => router.push('/try/gojuon')}
            className="bg-gray-600 hover:bg-gray-700 text-white"
          >
            ← 返回
          </Button>
        </div>
      </div>
    </div>
  )
}

export default GojuonPracticeTrialPage

